import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../state/authContext";
import { canAccessAdminDashboard, getGroupAdminDashboardPath } from "../lib/dashboardRoutes";

const decodeJwtPayload = (token) => {
  if (!token) return null;
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice((base64.length + 3) % 4);
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
};

const getSupabaseStorageKey = () => {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (!url) return null;
  try {
    const { hostname } = new URL(url);
    const ref = hostname.split(".")[0];
    return ref ? `sb-${ref}-auth-token` : null;
  } catch (_) {
    return null;
  }
};

const getCachedAccessToken = () => {
  if (typeof localStorage === "undefined") return null;
  const key = getSupabaseStorageKey();
  if (!key) return null;
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed?.access_token || parsed?.currentSession?.access_token || null;
  } catch (_) {
    return null;
  }
};

const getCachedSessionUserRole = () => {
  if (typeof localStorage === "undefined") return null;
  const key = getSupabaseStorageKey();
  if (!key) return null;
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const user = parsed?.user || parsed?.currentSession?.user || null;
    return user?.app_metadata?.role || user?.user_metadata?.role || null;
  } catch (_) {
    return null;
  }
};

const normalizeRole = (value) => {
  if (!value || value === "authenticated") return null;
  return value;
};

export default function AdminDashboardGuard({ children }) {
  const { user, role, groupMemberships, loading } = useAuth();
  const loc = useLocation();

  const tokenPayload = decodeJwtPayload(getCachedAccessToken());
  const tokenRole =
    tokenPayload?.role ||
    tokenPayload?.app_metadata?.role ||
    tokenPayload?.user_metadata?.role ||
    null;
  const cachedUserRole = getCachedSessionUserRole();
  const effectiveRole =
    normalizeRole(role) ??
    normalizeRole(tokenRole) ??
    normalizeRole(cachedUserRole) ??
    normalizeRole(user?.app_metadata?.role) ??
    normalizeRole(user?.user_metadata?.role) ??
    null;

  const hasGroupAdminMembership = Boolean(
    user &&
      Array.isArray(groupMemberships) &&
      groupMemberships.some((m) => m.role_in_group === "group_admin")
  );
  const isLoading =
    loading ||
    (user && groupMemberships == null) ||
    (user && effectiveRole === null && !hasGroupAdminMembership);
  if (isLoading) return <div className="p-6">Laddar…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;

  const allowed = canAccessAdminDashboard({ role: effectiveRole, groupMemberships });
  if (!allowed) {
    const groupAdminPath = getGroupAdminDashboardPath(groupMemberships);
    if (groupAdminPath) {
      return <Navigate to={groupAdminPath} replace />;
    }
    return <div className="p-6">Åtkomst nekad.</div>;
  }

  return children;
}
